/**
 * Corrected time.
 *
 * Cooldowns, blackout windows and lockdowns are all evaluated against the server's clock,
 * not the device's. A client running a few minutes fast would otherwise let a trader back
 * in before a cooldown had actually expired — the exact moment the rule exists to cover.
 *
 * The offset is measured against the snapshot's serverTime; elapsed time between snapshots
 * comes from performance.now(), which is monotonic and unaffected by the user or NTP
 * adjusting the wall clock mid-session.
 */
export class ServerClock {
  #offsetMs = 0
  #anchorPerf = 0
  #anchored = false

  /** Call on every snapshot. */
  sync(serverTimeIso: string): void {
    const serverMs = Date.parse(serverTimeIso)
    if (Number.isNaN(serverMs)) return

    this.#anchorPerf = performance.now()
    this.#offsetMs = serverMs - Date.now()
    this.#anchored = true
  }

  nowMs(): number {
    if (!this.#anchored) {
      // Never silently fall back to device time for a rule decision. The caller shows
      // "Checking…" until the first snapshot lands.
      throw new Error('ServerClock used before the first snapshot')
    }
    return Date.now() + this.#offsetMs
  }

  /** Monotonic milliseconds since the last sync — used for staleness, not for rules. */
  sinceSync(): number {
    return this.#anchored ? performance.now() - this.#anchorPerf : Number.POSITIVE_INFINITY
  }

  get anchored(): boolean {
    return this.#anchored
  }

  /** Rough skew between this device and the server, for diagnostics. */
  get offsetMs(): number {
    return this.#offsetMs
  }
}
